"use client";

import { Separator } from "@/components/ui/separator";
import { useGetActivityDiffs } from "@/hooks/useActivityDiffs";
import { cn } from "@/lib/utils";
import {
  useAiContextStore,
  useEngSpecOpenedFile,
  useOpenedAiChatEngSpec,
} from "@/store/zustand/aiContextStore";
import {
  EditorContext,
  EditorFile,
  useCodebaseStore,
} from "@/store/zustand/codebaseStore";
import useLayoutStore, {
  LayoutMode,
  LeftTab,
} from "@/store/zustand/layoutStore";
import { X } from "lucide-react";
import { useEffect } from "react";
import { Commit } from "./Commit";
import CodePanel from "@/components/CodeEditors/CodePanel";
import { applyTextEditorToContents } from "../chat/useApplyChatBlock";
import { getUnexpiredToken, useCanvasId } from "@/utils/utils";
import { useSession } from "@clerk/clerk-react";

interface EngSpecItem {
  id: string;
}

export interface EngSpecFileDiff extends EngSpecItem {
  summary?: string;

  // Old format before Jan 20, 2025
  // Remove after a few days
  filePath?: string;

  // If given - terminal command
  termCommand?: string;

  // If given - restart dev server
  requiresRestart?: boolean;

  // If given - sql migration
  runSqlMigration?: string;

  // If given - edge function
  deployEdgeFunction?: string;

  // This is the anthropic text editor tool
  command?: "create" | "str_replace" | "insert" | "overwrite";
  path?: string; // For all three
  old_str?: string; // For str_replace
  new_str?: string; // For str_replace
  insert_line?: number; // For insert
  file_text?: string; // For create
  replace_all?: boolean; // For str_replace

  executedActivityId?: string;
  change?: {
    newFullFileContent: string;
    originalContents?: string;
    streaming?: boolean;
  };

  // For storyboards only
  storyboardName?: string;
  positionInCanvas?: { x: number; y: number };
  size?: { width: number; height: number };
  topLeftCorner?: { x: number; y: number };

  runConvexSync?: boolean;

  hasChange?: boolean;
}
export interface EngSpecCommit extends EngSpecItem {
  feature: string;
  description: string;
  changes: EngSpecFileDiff[];
  filenamesReadForContext?: string[];
}
export interface EngSpecJson {
  commits: EngSpecCommit[];
}

const EngSpecTab = () => {
  const canvasId = useCanvasId();

  // Prioritize the streamed eng spec over the one from the database
  const aiChatEngSpec = useOpenedAiChatEngSpec();
  const closeAiChatEngSpec = useAiContextStore(
    (state) => state.closeAiChatEngSpec,
  );

  // Code diff panel state
  const codeDiffPanelOpen = useAiContextStore(
    (state) => state.codeDiffPanelOpen,
  );
  const closeCodeDiffPanel = useAiContextStore(
    (state) => state.closeCodeDiffPanel,
  );

  const setNewLayout = useLayoutStore((state) => state.setNewLayout);

  const { file: engSpecOpenedFile } = useEngSpecOpenedFile();
  const showAiCodeChange = useLayoutStore((state) => state.showAiCodeChange);
  const setShowAiCodeChange = useLayoutStore(
    (state) => state.setShowAiCodeChange,
  );

  const setEngSpecFiles = useCodebaseStore((state) => state.setEngSpecFiles);
  const clearEngSpecFiles = useCodebaseStore(
    (state) => state.clearEngSpecFiles,
  );

  const fileToContentsFromApi = useCodebaseStore(
    (state) => state.fileToContents,
  );

  const { session } = useSession();

  const getActivityDiffs = useGetActivityDiffs();

  const addFileChangesToCodeEditor = async (fileChanges: EngSpecFileDiff[]) => {
    if (!showAiCodeChange) {
      setShowAiCodeChange(true);
    }

    const files: EditorFile[] = [];
    const pathToExistingFile = {};

    for (const fileChange of fileChanges) {
      // HACK Jan 20, 2025 -> for old messages the path is under filePath, not path, reassign
      // Remove after a few days
      const filePath = fileChange.filePath || fileChange.path;

      if (!filePath || (!fileChange.change && !fileChange.command)) continue;

      // Handle activity diffs first
      if (fileChange.executedActivityId && !pathToExistingFile[filePath]) {
        const { calculatedDiffs, diffErrors } = getActivityDiffs(
          fileChange.executedActivityId,
        );

        const matchingDiff = calculatedDiffs?.find(
          (diff) => diff.filePath?.split(canvasId + "/")[1] === filePath,
        );

        if (matchingDiff && !diffErrors?.length) {
          const newFile = {
            id: `${fileChange.id}`,
            filename: filePath,
            keySuffix: "-0",
            calculatedDiff: matchingDiff,
            hardOpened: true,
            context: EditorContext.EngSpec,
            appliedDiffActivityId: fileChange.executedActivityId,
          };

          files.push(newFile);
          pathToExistingFile[filePath] = newFile;
          continue;
        }
      }

      // Get current content for this file
      let currentContent = fileToContentsFromApi[filePath] || "";
      let existingFile = pathToExistingFile[filePath];

      // Process the change
      if (fileChange.change?.newFullFileContent) {
        currentContent = fileChange.change.newFullFileContent;
      } else if (fileChange.command) {
        const baseContent =
          existingFile?.suggestedDiff ||
          fileToContentsFromApi[filePath] ||
          currentContent ||
          "";

        const { newContents, succeeded } = await applyTextEditorToContents(
          {
            command: fileChange.command,
            path: filePath,
            old_str: fileChange.old_str,
            new_str: fileChange.new_str,
            insert_line: fileChange.insert_line,
            file_text: fileChange.file_text,
            replace_all: fileChange.replace_all,
          },
          baseContent,
          canvasId,
          (await getUnexpiredToken(session)) || "",
        );

        if (succeeded) {
          currentContent = newContents;
        }
      }

      // Update or create file entry
      if (existingFile) {
        existingFile.suggestedDiff = currentContent;
      } else {
        existingFile = {
          id: `${fileChange.id}`,
          filename: filePath,
          keySuffix: "-0", // Since we're processing sequentially, we don't need to track multiple changes per file
          suggestedDiff: currentContent,
          hideOriginalCode: Boolean(fileChange?.change?.streaming),
          hardOpened: true,
          context: EditorContext.EngSpec,
          appliedDiffActivityId: fileChange.executedActivityId,
        };
        files.push(existingFile);
        pathToExistingFile[filePath] = existingFile;
      }
    }

    if (files.length > 0) {
      setEngSpecFiles({ files });
    }
  };

  useEffect(() => {
    if (!session) {
      return;
    }

    // Skip auto-processing when code diff panel is open
    if (codeDiffPanelOpen) {
      return;
    }

    if (engSpecOpenedFile?.change) {
      addFileChangesToCodeEditor([engSpecOpenedFile]);
    } else {
      if (showAiCodeChange) {
        setShowAiCodeChange(false);
      }

      clearEngSpecFiles();
    }
  }, [engSpecOpenedFile, fileToContentsFromApi, codeDiffPanelOpen]);

  // Show code diff if panel is open
  if (codeDiffPanelOpen) {
    return (
      <div className="h-full w-full flex flex-col">
        <div className="flex flex-row w-full items-center p-4 border-b gap-1">
          <div className="flex flex-col justify-between items-start grow">
            <div className={cn("font-black text-sm font-semibold")}>
              Change made from chat
            </div>
          </div>
          <div
            className="h-7 flex items-center"
            onClick={() => {
              closeCodeDiffPanel();
            }}
          >
            <X className="w-4 h-4 cursor-pointer text-secondary-800 hover:text-secondary-900 ml-1" />
          </div>
        </div>
        <div className="flex-1">
          <CodePanel />
        </div>
      </div>
    );
  }

  return (
    <div className="h-full w-full p-4 flex overflow-y-auto hide-scrollbar flex-col gap-4">
      <div className="flex flex-row w-full items-center grow-0 gap-1">
        <div className="flex flex-col justify-between items-start grow">
          <div className={cn("font-black text-sm font-semibold")}>
            {"Tempo AI"}
          </div>
        </div>
        <div
          className="h-7 flex items-center"
          onClick={() => {
            closeAiChatEngSpec();
            setNewLayout(LeftTab.AIChat, LayoutMode.Design);
          }}
        >
          <X className="w-4 h-4 cursor-pointer text-secondary-800 hover:text-secondary-900 ml-1" />
        </div>
      </div>

      <Separator />

      <div className={cn("grow flex flex-col gap-6")}>
        {aiChatEngSpec?.commits?.map((commit, index) => {
          return <Commit key={commit.id} number={index + 1} commit={commit} />;
        })}
      </div>
    </div>
  );
};

export default EngSpecTab;
